import { primaryRoleLabel } from '@sistemas/contracts'
import { cn, getInitials } from '@sistemas/ui'
import { BarChart3, ChevronRight, CircleHelp, KeyRound, LogOut, MessageCircle, PanelLeftClose, PanelLeftOpen, Plus, Search } from 'lucide-react'
import * as React from 'react'
import { useLocation } from 'react-router-dom'
import { Toaster } from 'sonner'

import { ShellChromeProvider, useShellChrome } from '@/components/shell-chrome'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { ThemeToggle } from '@/components/theme-toggle'
import { DeviceGate } from '@/components/desktop-only'
import { MobileNav } from '@/components/mobile-nav'
import { useIsMobile, visibleMobileNav } from '@/lib/mobile'
import { useSession } from '@/lib/session'
import { SidebarNav } from '@/components/sidebar-nav'
import { useNavPins } from '@/lib/nav-pins'
import { breadcrumbs, visibleNavGroups } from '@/lib/navigation'
import { QuickActivityProvider, useQuickActivity } from '@/modules/projects/QuickActivityFlow'
import { useCore } from '@/modules/projects/store'
import { useSidebarCollapsed } from '@/lib/use-sidebar-collapsed'
import { LiveIndicator } from '@/components/live-indicator'
import { NotificationBell } from '@/modules/notifications/NotificationBell'

/* Diálogos da conta: só baixam quando alguém abre (JS inicial, Onda 6.2). */
const ApiTokensDialog = React.lazy(() =>
  import('@/modules/people/ApiTokensDialog').then((m) => ({ default: m.ApiTokensDialog })),
)
const ProfilePhotoDialog = React.lazy(() =>
  import('@/modules/people/ProfilePhotoDialog').then((m) => ({ default: m.ProfilePhotoDialog })),
)
const WhatsAppSettingsDialog = React.lazy(() =>
  import('@/modules/notifications/WhatsAppSettingsDialog').then((m) => ({ default: m.WhatsAppSettingsDialog })),
)
/** Perfil do celular (Onda 6.1): também é diálogo, também sob demanda. */
const ProfileSheet = React.lazy(() => import('@/components/profile-sheet'))

/**
 * O time trabalha em Windows; mostrar ⌘ seria instrução errada.
 * Avaliado uma vez — a plataforma não muda durante a sessão.
 */
const shortcutKey =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
    ? '⌘'
    : 'Ctrl '

/**
 * Shell do Hub SISTEMAS.
 *
 * Replica o vocabulário visual de SYS/apps/web/src/components/Layout.tsx,
 * que é a referência do ecossistema. Medidas fixadas em
 * docs/SDD/18-design-system.md §4 — sidebar 64/256 iniciando recolhida,
 * topbar h-14 sticky, item ativo com barra bg-primary à esquerda,
 * rótulos de grupo em mono 10px uppercase tracking 0.18em.
 *
 * Divergir daqui exige justificativa escrita antes do merge.
 *
 * Única exceção à moldura: o modo apresentação do Caderno (20 §4.4), pedido
 * pela página via `ShellChrome` — sidebar e topbar saem e o conteúdo perde a
 * largura máxima, porque a nota vai para o telão.
 *
 * Celular (Onda 6.1, abaixo de 768 px): sem sidebar; barra inferior com Notas,
 * Projetos, Chamados, Agenda e o perfil (`MobileNav`). A topbar fica só com o
 * nome da tela, o tempo real e o sino. Rota fora de `isMobileRoute` mostra
 * "Esta tela foi feita para o computador" no lugar da página.
 */
export function AppShell({ children }: { children: React.ReactNode }) {
  return (
    <ShellChromeProvider>
      <QuickActivityProvider>
        <ShellFrame>{children}</ShellFrame>
      </QuickActivityProvider>
    </ShellChromeProvider>
  )
}

function ShellFrame({ children }: { children: React.ReactNode }) {
  const { presenting } = useShellChrome()
  const [collapsed, toggleCollapsed] = useSidebarCollapsed()
  const location = useLocation()
  const { member, roles, signOut } = useSession()
  const { state } = useCore()
  const quickActivity = useQuickActivity()

  const navGroups = React.useMemo(() => visibleNavGroups(roles), [roles])
  const navPins = useNavPins(member?.id)
  const trail = React.useMemo(() => {
    const base = breadcrumbs(location.pathname)
    // Sub-rota do projeto: a trilha ganha o nome do registro.
    const projectMatch = /^\/projetos\/([^/]+)/.exec(location.pathname)
    const project = projectMatch
      ? state.projects.find((p) => p.id === projectMatch[1])
      : undefined
    return project ? [...base, project.name] : base
  }, [location.pathname, state.projects])
  const roleLabel = primaryRoleLabel(roles)
  const displayName = member?.displayName ?? 'Carregando...'
  const myFicha = member ? state.members.find((m) => m.id === member.id) : undefined
  const [editingPhoto, setEditingPhoto] = React.useState(false)
  const [editingWhatsApp, setEditingWhatsApp] = React.useState(false)
  const [editingTokens, setEditingTokens] = React.useState(false)
  const isMobile = useIsMobile()
  const [profileOpen, setProfileOpen] = React.useState(false)
  const mobileNav = React.useMemo(() => visibleMobileNav(roles), [roles])
  // Do Perfil para um dos diálogos da conta: um modal por vez.
  const fromProfile = (open: (value: boolean) => void) => () => {
    setProfileOpen(false)
    open(true)
  }

  return (
    // `dvh`: no celular, `100vh` conta a barra de endereço e a barra inferior
    // ficaria escondida atrás dela.
    <div className="flex h-dvh w-screen overflow-hidden bg-background text-foreground">
      <aside
        data-collapsed={collapsed}
        className={cn(
          'hidden h-full flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-[width] duration-200 md:flex',
          collapsed ? 'w-[64px]' : 'w-[256px]',
          presenting && 'md:hidden',
        )}
      >
        <div
          className={cn(
            'flex h-14 items-center gap-2.5 border-b border-sidebar-border px-3',
            collapsed && 'justify-center px-0',
          )}
        >
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <BarChart3 className="h-3.5 w-3.5" />
          </div>
          {!collapsed && (
            <div className="flex min-w-0 flex-col">
              <span className="text-[13px] font-semibold leading-tight tracking-tight text-foreground">
                Flexibase SISTEMAS
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                Hub do departamento
              </span>
            </div>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto px-2 py-3">
          <button
            type="button"
            onClick={quickActivity.open}
            className={cn(
              'mb-3 flex h-8 w-full items-center gap-2 rounded-md border border-sidebar-border bg-sidebar-accent/45 px-2 text-[13px] font-medium text-sidebar-foreground transition-colors hover:bg-sidebar-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              collapsed && 'justify-center px-0',
            )}
            title={collapsed ? 'Nova atividade (Alt+N)' : 'Alt+N'}
            aria-keyshortcuts="Alt+N"
          >
            <Plus className="h-3.5 w-3.5" />
            {!collapsed && <span>Nova atividade</span>}
          </button>

          <SidebarNav groups={navGroups} pathname={location.pathname} collapsed={collapsed} pins={navPins} />
        </nav>

        <div className="mt-auto border-t border-sidebar-border p-2">
          {/*
            Recolhida, permanece só o avatar — a identidade de quem está logado
            não some. Mesmo comportamento do PMC/AppSidebar.tsx.
          */}
          <div
            className={cn(
              'mb-2 flex items-center gap-2 rounded-md px-1 py-1.5',
              collapsed && 'justify-center px-0',
            )}
          >
            {/* A foto é o atalho para trocá-la ("Minha foto"). */}
            <button
              type="button"
              onClick={() => setEditingPhoto(true)}
              disabled={!myFicha}
              title={collapsed ? `${displayName} · trocar foto` : 'Trocar foto'}
              aria-label="Trocar minha foto de perfil"
              className="shrink-0 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Avatar
                person={
                  myFicha ?? { fullName: displayName, initials: getInitials(displayName) }
                }
                size="lg"
                showTitle={false}
              />
            </button>
            {!collapsed && (
              <>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-semibold leading-tight">
                    {displayName}
                  </p>
                  <p className="truncate font-mono text-[10px] text-muted-foreground">
                    {roleLabel}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  title="Acesso do Claude (MCP)"
                  aria-label="Acesso do Claude (MCP)"
                  onClick={() => setEditingTokens(true)}
                >
                  <KeyRound className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  title="WhatsApp e avisos"
                  aria-label="WhatsApp e avisos"
                  onClick={() => setEditingWhatsApp(true)}
                >
                  <MessageCircle className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  title="Sair"
                  aria-label="Sair"
                  onClick={() => void signOut()}
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleCollapsed}
            className={cn(
              'w-full justify-start gap-2 text-muted-foreground',
              collapsed && 'justify-center px-0',
            )}
            title={collapsed ? 'Expandir menu' : 'Recolher menu'}
            aria-expanded={!collapsed}
          >
            {collapsed ? (
              <PanelLeftOpen className="h-3.5 w-3.5" />
            ) : (
              <PanelLeftClose className="h-3.5 w-3.5" />
            )}
            {!collapsed && <span className="text-[12px]">Recolher</span>}
          </Button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/*
          Ordem da topbar copiada de PMC/apps/web/src/components/Topbar.tsx:
          trilha à esquerda, e o bloco ajuda → busca → tema empurrado à direita
          pelo `ml-auto` no botão de ajuda.
        */}
        <header
          className={cn(
            'sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border bg-background/90 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/70',
            presenting && 'hidden',
          )}
        >
          <nav
            aria-label="Trilha"
            className="hidden items-center gap-1.5 text-[12px] text-muted-foreground md:flex"
          >
            {trail.map((crumb, index) => (
              <React.Fragment key={crumb}>
                {index > 0 && <ChevronRight className="h-3 w-3 opacity-50" aria-hidden />}
                <span
                  className={cn(
                    'rounded px-1',
                    index === trail.length - 1 && 'text-foreground',
                  )}
                >
                  {crumb}
                </span>
              </React.Fragment>
            ))}
          </nav>
          {/* Celular: no lugar da trilha, só o nome da tela. */}
          <p className="min-w-0 flex-1 truncate text-[15px] font-semibold tracking-tight md:hidden">
            {trail.at(-1)}
          </p>

          <button
            type="button"
            aria-label="Ajuda"
            title="Ajuda"
            className="ml-auto hidden h-8 w-8 shrink-0 items-center justify-center rounded-md border border-input bg-background text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:inline-flex"
          >
            <CircleHelp className="h-4 w-4" />
          </button>

          <button
            type="button"
            className="hidden h-8 w-full max-w-md items-center gap-2 rounded-md border border-input bg-background px-2.5 text-[12px] text-muted-foreground transition-colors hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:max-w-[440px] md:flex"
          >
            <Search className="h-3.5 w-3.5" aria-hidden />
            <span className="flex-1 truncate text-left">
              Buscar atividade, projeto ou pessoa
            </span>
            <kbd className="hidden items-center gap-0.5 rounded border bg-muted px-1.5 py-0.5 font-mono text-[10px] font-medium text-muted-foreground sm:inline-flex">
              {shortcutKey}K
            </kbd>
          </button>

          <LiveIndicator />
          {/* No celular o sino ganha alvo de toque de 44 px; o tema vai para o Perfil. */}
          <span className="contents max-md:[&>button]:size-11">
            <NotificationBell />
          </span>
          <ThemeToggle className="hidden md:inline-flex" />
        </header>
        {/* Duas alturas, e a PÁGINA escolhe qual quer (ver `.page-canvas` em
            tokens.css):
              · padrão — `min-h-full`: cresce com o conteúdo e o <main> rola,
                preservando o respiro final da página comprida;
              · com `page-fill` na raiz — altura exata, que é o que dá teto ao
                `flex-1` interno e faz as colunas rolarem por dentro.
            Uma altura só não serve para as duas: `min-height` nunca limita, e
            `height` fixo tira o respiro do fim de quem é comprido. */}
        <main className="scroll-area min-h-0 flex-1 overflow-y-auto">
          <div
            className={cn(
              'page-canvas mx-auto flex min-h-full w-full flex-col',
              presenting
                ? 'px-4 py-4 sm:px-6'
                : 'max-w-[1440px] px-4 py-5 sm:px-5 lg:px-6 lg:py-6 xl:px-8 xl:py-8',
            )}
          >
            <DeviceGate pathname={location.pathname} search={location.search}>
              {children}
            </DeviceGate>
          </div>
        </main>
        {!presenting && (
          <MobileNav
            items={mobileNav}
            pathname={location.pathname}
            onOpenProfile={() => setProfileOpen(true)}
            profile={myFicha ?? { fullName: displayName, initials: getInitials(displayName) }}
          />
        )}
      </div>
      {/*
        Feedback de escrita (doc 18 §5: sonner). Cores só por token — o toast
        de erro usa `--destructive`, o padrão usa `--card`/`--border`. Sem
        `richColors`, que traria uma paleta própria.
      */}
      <Toaster
        // No celular o rodapé é da barra de navegação.
        position={isMobile ? 'top-center' : 'bottom-right'}
        theme="system"
        closeButton
        toastOptions={{
          unstyled: true,
          classNames: {
            toast:
              'flex w-[min(356px,calc(100vw-2rem))] items-start gap-2.5 rounded-md border border-border bg-card p-3 text-[12px] text-foreground shadow-lg',
            content: 'min-w-0 flex-1',
            title: 'font-medium',
            description: 'text-muted-foreground',
            // "Desfazer" e afins: ação curta, na mesma linha, sem quebrar a palavra.
            actionButton:
              'shrink-0 self-center whitespace-nowrap rounded px-2 py-1 font-medium text-primary transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            error: 'border-destructive/40 [&_[data-icon]]:text-destructive',
            success: '[&_[data-icon]]:text-success',
            closeButton:
              'absolute -left-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full border border-border bg-card text-muted-foreground hover:text-foreground',
          },
        }}
      />
      <React.Suspense fallback={null}>
        {profileOpen && (
          <ProfileSheet
            member={myFicha}
            displayName={displayName}
            roleLabel={roleLabel}
            onClose={() => setProfileOpen(false)}
            onEditPhoto={myFicha ? fromProfile(setEditingPhoto) : undefined}
            onWhatsApp={fromProfile(setEditingWhatsApp)}
            onTokens={fromProfile(setEditingTokens)}
            onSignOut={() => void signOut()}
          />
        )}
        {editingTokens && <ApiTokensDialog onClose={() => setEditingTokens(false)} />}
        {editingWhatsApp && <WhatsAppSettingsDialog onClose={() => setEditingWhatsApp(false)} />}
        {editingPhoto && myFicha && (
          <ProfilePhotoDialog member={myFicha} onClose={() => setEditingPhoto(false)} />
        )}
      </React.Suspense>
    </div>
  )
}
